import { useState, useEffect, type FC, type FormEvent } from 'react';
import {
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
  Users,
  Clock,
  Zap,
} from 'lucide-react';
import type {
  CampaignResponse,
  CreateCampaignRequest,
  MailboxResponse,
  UpdateCampaignRequest,
  CampaignValidationResult,
} from '@antigravity/types';
import { apiClient, ApiClientError } from '../../services/api-client';

interface CampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (campaign: CampaignResponse) => void;
  campaign?: CampaignResponse | null;
  mailboxes: MailboxResponse[];
  onOpenConnectSender?: () => void;
}

export const CampaignModal: FC<CampaignModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaign,
  mailboxes,
  onOpenConnectSender,
}) => {
  const isEditing = Boolean(campaign);

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromMailboxId, setFromMailboxId] = useState('');
  const [recipientsText, setRecipientsText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [textContent, setTextContent] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [activeTab, setActiveTab] = useState<'html' | 'text'>('html');

  // Templates, Audience & Dispatch Mode
  const [templates, setTemplates] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [dispatchMode, setDispatchMode] = useState<'NOW' | 'SCHEDULE' | 'DRAFT'>('NOW');

  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationResult, setValidationResult] =
    useState<CampaignValidationResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Load templates and contacts
      (async () => {
        try {
          const [tplData, ctsData] = await Promise.all([
            apiClient.getTemplates().catch(() => []),
            apiClient.getContacts({ limit: 1000 }).catch(() => ({ contacts: [] })),
          ]);
          setTemplates(tplData || []);
          setContacts(ctsData?.contacts || []);
        } catch (err) {
          console.error('Failed to prefetch templates/contacts:', err);
        }
      })();
    }

    if (campaign) {
      setName(campaign.name);
      setSubject(campaign.subject);
      setFromMailboxId(campaign.fromMailboxId);
      setHtmlContent(campaign.htmlContent || '');
      setTextContent(campaign.textContent || '');
      setSelectedTemplateId(campaign.templateId || '');
      setScheduledAt(
        campaign.scheduledAt ? campaign.scheduledAt.slice(0, 16) : '',
      );
      if (campaign.scheduledAt) {
        setDispatchMode('SCHEDULE');
      } else if (campaign.status === 'READY') {
        setDispatchMode('NOW');
      } else {
        setDispatchMode('DRAFT');
      }

      const config = campaign.recipientSourceConfig as {
        recipients?: string[];
      } | null;
      if (config?.recipients && Array.isArray(config.recipients)) {
        setRecipientsText(config.recipients.join('\n'));
      } else {
        setRecipientsText('');
      }
    } else {
      setName('');
      setSubject('');
      setFromMailboxId(mailboxes.length > 0 ? mailboxes[0].id : '');
      setRecipientsText('');
      setHtmlContent('');
      setTextContent('');
      setScheduledAt('');
      setSelectedTemplateId('');
      setDispatchMode('NOW');
    }
    setErrorMessage(null);
    setValidationResult(null);
  }, [campaign, mailboxes, isOpen]);

  if (!isOpen) return null;

  const parseRecipients = (): string[] => {
    return recipientsText
      .split(/[\n,;]+/)
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const t = templates.find((item) => item.id === templateId);
    if (t) {
      if (t.subject && !subject) setSubject(t.subject);
      if (t.htmlContent) setHtmlContent(t.htmlContent);
      if (t.textContent) setTextContent(t.textContent);
    }
  };

  const handleImportContacts = () => {
    if (contacts.length === 0) return;
    const activeEmails = contacts
      .filter((c) => c.status === 'SUBSCRIBED' || !c.status)
      .map((c) => c.email);
    const existing = parseRecipients();
    const merged = Array.from(new Set([...existing, ...activeEmails]));
    setRecipientsText(merged.join('\n'));
  };

  const handleSave = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);

    if (!name.trim()) {
      setErrorMessage('Campaign name is required');
      return;
    }
    if (!subject.trim()) {
      setErrorMessage('Subject line is required');
      return;
    }
    if (!fromMailboxId) {
      setErrorMessage('Please select a sender mailbox');
      return;
    }

    const recipients = parseRecipients();

    setIsLoading(true);
    try {
      let finalCampaign: CampaignResponse;
      const isSchedule = dispatchMode === 'SCHEDULE' && scheduledAt;

      if (isEditing && campaign) {
        const payload: UpdateCampaignRequest = {
          name: name.trim(),
          subject: subject.trim(),
          fromMailboxId,
          htmlContent: htmlContent || undefined,
          textContent: textContent || undefined,
          templateId: selectedTemplateId || undefined,
          recipientSourceType: 'MANUAL',
          recipientSourceConfig: { recipients },
          scheduledAt: isSchedule ? new Date(scheduledAt).toISOString() : undefined,
        };
        finalCampaign = await apiClient.updateCampaign(campaign.id, payload);
      } else {
        const payload: CreateCampaignRequest = {
          name: name.trim(),
          subject: subject.trim(),
          fromMailboxId,
          htmlContent: htmlContent || undefined,
          textContent: textContent || undefined,
          templateId: selectedTemplateId || undefined,
          recipientSourceType: 'MANUAL',
          recipientSourceConfig: { recipients },
          scheduledAt: isSchedule ? new Date(scheduledAt).toISOString() : undefined,
        };
        finalCampaign = await apiClient.createCampaign(payload);
      }

      // If user selected "Send Now", automatically transition to READY and dispatch
      if (dispatchMode === 'NOW') {
        try {
          if (finalCampaign.status !== 'READY') {
            await apiClient.transitionCampaignStatus(finalCampaign.id, {
              status: 'READY',
            });
            finalCampaign.status = 'READY';
          }
          await apiClient.dispatchCampaign(finalCampaign.id);
          finalCampaign.status = 'QUEUED';
        } catch (dispatchErr: any) {
          console.warn('Could not auto-dispatch campaign:', dispatchErr);
          if (dispatchErr instanceof ApiClientError) {
            setErrorMessage(`Campaign saved, but dispatch failed: ${dispatchErr.message}`);
            return;
          }
        }
      }

      onSuccess(finalCampaign);
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Failed to save campaign. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = async () => {
    setErrorMessage(null);
    setValidationResult(null);

    if (!isEditing || !campaign) {
      // For new campaign, auto-save as draft first before validating
      setErrorMessage('Please save the campaign draft before running readiness checks.');
      return;
    }

    setIsValidating(true);
    try {
      const result = await apiClient.validateCampaign(campaign.id);
      setValidationResult(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Failed to perform validation check.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-2xl rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl p-6 my-8">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">
              {isEditing ? 'Edit Campaign Draft' : 'Create New Campaign'}
            </h3>
            <p className="text-xs text-slate-400">
              {isEditing
                ? 'Update campaign properties and verify readiness'
                : 'Configure email campaign properties and audience'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-200 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Validation Result Banner */}
        {validationResult && (
          <div
            className={`mt-4 p-3.5 rounded-xl border text-xs ${
              validationResult.valid
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-200'
                : 'bg-rose-500/10 border-rose-500/20 text-rose-200'
            }`}
          >
            <div className="font-semibold flex items-center gap-2 mb-1">
              {validationResult.valid ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Validation Passed: Ready for Dispatch</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-rose-400" />
                  <span>
                    Validation Issues ({validationResult.errors.length} error
                    {validationResult.errors.length === 1 ? '' : 's'}):
                  </span>
                </>
              )}
            </div>
            {validationResult.errors.length > 0 && (
              <ul className="list-disc list-inside space-y-0.5 pl-1 text-[11px] opacity-90">
                {validationResult.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSave} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                Campaign Name <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q4 Product Announcement"
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs focus:outline-none focus:border-brand-500"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-300">
                  Sender Mailbox <span className="text-rose-400">*</span>
                </label>
                {onOpenConnectSender && (
                  <button
                    type="button"
                    onClick={onOpenConnectSender}
                    className="text-[11px] text-brand-400 hover:text-brand-300 font-medium underline flex items-center gap-1 transition"
                  >
                    <span>+ Connect Existing Email (3 Ways)</span>
                  </button>
                )}
              </div>
              <select
                value={fromMailboxId}
                onChange={(e) => setFromMailboxId(e.target.value)}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs focus:outline-none focus:border-brand-500"
                required
              >
                <option value="">Select a sender mailbox...</option>
                {mailboxes.map((mb) => (
                  <option key={mb.id} value={mb.id}>
                    {mb.displayName ? `${mb.displayName} <${mb.emailAddress}>` : mb.emailAddress}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-slate-300">
                Subject Line <span className="text-rose-400">*</span>
              </label>
              {templates.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-brand-400" />
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => handleSelectTemplate(e.target.value)}
                    className="text-[11px] bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-brand-300 focus:outline-none focus:border-brand-500"
                  >
                    <option value="">Insert from Template Library...</option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Introducing our newest features"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs focus:outline-none focus:border-brand-500"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-300">
                Recipients (Manual List or Uploaded Audience)
              </label>
              <div className="flex items-center gap-2">
                {contacts.length > 0 && (
                  <button
                    type="button"
                    onClick={handleImportContacts}
                    className="text-[11px] text-purple-300 hover:text-purple-200 font-semibold flex items-center gap-1 bg-purple-500/10 hover:bg-purple-500/20 px-2 py-0.5 rounded-lg border border-purple-500/30 transition"
                  >
                    <Users className="w-3 h-3 text-purple-400" />
                    <span>Import {contacts.length} Contacts</span>
                  </button>
                )}
                <span className="text-[11px] text-slate-400">
                  {parseRecipients().length} email(s) detected
                </span>
              </div>
            </div>
            <textarea
              rows={3}
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              placeholder="Enter recipient email addresses, separated by commas or newlines..."
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs font-mono focus:outline-none focus:border-brand-500 resize-y"
            />
          </div>

          {/* Body Content Editor */}
          <div>
            <div className="flex items-center justify-between mb-2 border-b border-slate-800 pb-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('html')}
                  className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${
                    activeTab === 'html'
                      ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  HTML Content
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('text')}
                  className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${
                    activeTab === 'text'
                      ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Plain Text
                </button>
              </div>
              <span className="text-[11px] text-slate-400">
                HTML sanitized automatically before save
              </span>
            </div>

            {activeTab === 'html' ? (
              <textarea
                rows={6}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="<p>Hello {{firstName}},</p><p>We are thrilled to share...</p>"
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs font-mono focus:outline-none focus:border-brand-500 resize-y"
              />
            ) : (
              <textarea
                rows={6}
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                placeholder="Hello {{firstName}},&#10;&#10;We are thrilled to share..."
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs font-mono focus:outline-none focus:border-brand-500 resize-y"
              />
            )}
          </div>

          {/* Dispatch Timing Options */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-slate-300">
              Dispatch Action & Scheduling <span className="text-rose-400">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setDispatchMode('NOW')}
                className={`p-3 rounded-xl border text-left transition flex items-start gap-2.5 ${
                  dispatchMode === 'NOW'
                    ? 'bg-brand-600/20 border-brand-500 text-white shadow-lg shadow-brand-500/10'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <Zap className={`w-4 h-4 mt-0.5 ${dispatchMode === 'NOW' ? 'text-brand-400' : 'text-slate-500'}`} />
                <div>
                  <div className="text-xs font-bold text-slate-200">Send Now</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Dispatches immediately on save</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setDispatchMode('SCHEDULE')}
                className={`p-3 rounded-xl border text-left transition flex items-start gap-2.5 ${
                  dispatchMode === 'SCHEDULE'
                    ? 'bg-purple-600/20 border-purple-500 text-white shadow-lg shadow-purple-500/10'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <Clock className={`w-4 h-4 mt-0.5 ${dispatchMode === 'SCHEDULE' ? 'text-purple-400' : 'text-slate-500'}`} />
                <div>
                  <div className="text-xs font-bold text-slate-200">Schedule Later</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Automated queue dispatch</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setDispatchMode('DRAFT')}
                className={`p-3 rounded-xl border text-left transition flex items-start gap-2.5 ${
                  dispatchMode === 'DRAFT'
                    ? 'bg-slate-800 border-slate-600 text-white'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <FileText className={`w-4 h-4 mt-0.5 ${dispatchMode === 'DRAFT' ? 'text-slate-300' : 'text-slate-500'}`} />
                <div>
                  <div className="text-xs font-bold text-slate-200">Save Draft</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Hold for review and edits</div>
                </div>
              </button>
            </div>

            {dispatchMode === 'SCHEDULE' && (
              <div className="pt-2">
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-950 border border-purple-500/50 rounded-xl text-slate-100 text-xs focus:outline-none focus:border-purple-400"
                  required={dispatchMode === 'SCHEDULE'}
                />
                <p className="text-[11px] text-purple-300 mt-1">
                  The sovereign dispatch worker will queue recipients at this specific timestamp.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
            <div>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={isValidating || isLoading}
                  className="px-3 py-2 text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isValidating ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />
                  )}
                  Check Readiness
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-slate-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white rounded-xl shadow-lg shadow-brand-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
              >
                {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {dispatchMode === 'NOW'
                  ? 'Send Immediately'
                  : dispatchMode === 'SCHEDULE'
                  ? 'Schedule Campaign'
                  : isEditing
                  ? 'Save Changes'
                  : 'Save Draft'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
